<template>
  <el-dialog
    :title="$t('ticket.title')" :visible.sync="visible" append-to-body
    custom-class="support-ticket-dialog" :z-index="100001" top="5vh"
    :close-on-click-modal="false" :close-on-press-escape="!submitting"
    :show-close="!submitting" :before-close="beforeClose"
  >
    <div v-if="loading" role="status">{{ $t('ticket.loading') }}</div>
    <div v-else-if="loadError" role="alert">
      <p>{{ loadError }}</p>
      <el-button @click="loadFields">{{ $t('ticket.retry') }}</el-button>
    </div>
    <el-form v-else ref="form" :model="values" label-position="top" :disabled="submitting" @submit.native.prevent="submit">
      <el-form-item v-for="field in visibleFields" :key="field.id" :prop="String(field.id)" :rules="rules(field)" :label="field.type === 'checkbox' ? undefined : label(field)">
        <el-checkbox v-if="field.type === 'checkbox'" v-model="values[field.id]" class="ticket-checkbox">
          {{ label(field) }}{{ required(field) ? ' *' : '' }}
        </el-checkbox>
        <el-select v-else-if="isSelect(field)" v-model="values[field.id]" :multiple="field.type === 'multiselect'" filterable clearable :placeholder="$t('ticket.select')">
          <el-option v-for="option in options(field)" :key="option.value" :label="option.name" :value="option.value" />
        </el-select>
        <el-date-picker v-else-if="field.type === 'date'" v-model="values[field.id]" type="date" value-format="yyyy-MM-dd" />
        <el-input v-else v-model="values[field.id]" :type="['description', 'textarea'].includes(field.type) ? 'textarea' : 'text'" :rows="4" />
        <p v-if="field.description" class="ticket-description">{{ field.description }}</p>
      </el-form-item>
      <p v-if="submitError" role="alert" class="ticket-error">{{ submitError }}</p>
    </el-form>
    <span slot="footer">
      <el-button :disabled="submitting" @click="visible = false">{{ $t('ticket.cancel') }}</el-button>
      <el-button type="primary" :loading="submitting" :disabled="loading || !!loadError" @click="submit">{{ $t('ticket.submit') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { request } from '@/utils/utils'
import { ticketFields, visibleTicketFields, requiredTicketField, ticketPayload } from '@/utils/ticket'

export default {
  data () {
    return { visible: false, loading: false, submitting: false, loadError: '', submitError: '', fields: [], conditions: [], values: {} }
  },
  computed: {
    visibleFields () {
      return visibleTicketFields(this.fields, this.conditions, this.values)
    },
  },
  watch: {
    visibleFields (fields, previous) {
      const current = new Map(fields.map(field => [String(field.id), field]))
      const changed = []
      previous.forEach(field => {
        const next = current.get(String(field.id))
        if (!next) this.values[field.id] = this.emptyValue(field)
        if (!next || this.required(field) !== this.required(next)) changed.push(String(field.id))
      })
      if (changed.length) {
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.clearValidate(changed)
        })
      }
    },
  },
  methods: {
    required: requiredTicketField,
    emptyValue (field) { return field.type === 'checkbox' ? false : field.type === 'multiselect' ? [] : '' },
    label (field) { return field.title_in_portal || field.title },
    isSelect (field) { return ['tagger', 'multiselect', 'priority', 'tickettype'].includes(field.type) },
    options (field) { return field.custom_field_options || field.system_field_options || [] },
    rules (field) {
      return [{ required: this.required(field),
        validator: (rule, value, callback) => {
          const empty = value == null || (Array.isArray(value) ? !value.length : !String(value).trim())
          if (this.required(field) && (empty || (field.type === 'checkbox' && value !== true))) {
            return callback(new Error(this.$t('ticket.required')))
          }
          if (!empty) {
            let valid = true
            if (field.type === 'integer') valid = /^-?\d+$/.test(value)
            if (field.type === 'decimal') valid = /^-?\d+(\.\d+)?$/.test(value)
            if (field.type === 'regexp' && field.regexp_for_validation) {
              try { valid = new RegExp(field.regexp_for_validation).test(value) } catch (error) { valid = false }
            }
            if (!valid) return callback(new Error(this.$t('ticket.invalid')))
          }
          callback()
        },
        trigger: ['blur', 'change'] }]
    },
    open () {
      if (this.visible) return
      this.visible = true
      this.loadFields()
    },
    beforeClose (done) { if (!this.submitting) done() },
    async api (path, method, data) {
      const response = await request({
        url: `/api/dashboard/ticket/${path}`,
        method,
        data,
        params: { access_token: localStorage.getItem('Authorization') },
      })
      if (!response || !response.data) throw new Error(this.$t('ticket.networkError'))
      if (String(response.data.code) !== '10000') throw new Error(response.data.msg || this.$t('ticket.networkError'))
      return response.data.data
    },
    async loadFields () {
      if (this.loading) return
      this.loading = true
      this.loadError = ''
      this.submitError = ''
      try {
        const data = await this.api('fields', 'get')
        const fields = ticketFields(data.fields)
        const conditions = data.end_user_conditions || []
        if (!Array.isArray(conditions) || conditions.some(condition => !Array.isArray(condition.child_fields))) {
          throw new Error('Invalid ticket conditions response')
        }
        const values = {}
        fields.forEach(field => { values[field.id] = this.emptyValue(field) })
        this.values = values
        this.conditions = conditions
        this.fields = fields
      } catch (error) {
        this.loadError = this.$t('ticket.loadError')
      } finally {
        this.loading = false
      }
    },
    submit () {
      if (this.submitting || this.loading || this.loadError || !this.$refs.form) return
      this.$refs.form.validate(async valid => {
        if (!valid || this.submitting) return
        this.submitting = true
        this.submitError = ''
        try {
          await this.api('create', 'post', ticketPayload(this.visibleFields, this.values))
          this.visible = false
          this.$message.success(this.$t('ticket.success'))
        } catch (error) {
          this.submitError = error.message || this.$t('ticket.networkError')
        } finally {
          this.submitting = false
        }
      })
    },
  },
}
</script>

<style>
.support-ticket-dialog { width: 640px; max-width: calc(100vw - 32px); }
.support-ticket-dialog .el-dialog__body { max-height: 70vh; overflow-y: auto; padding: 16px 20px; }
.support-ticket-dialog .el-form--label-top .el-form-item__label { line-height: 20px; padding: 0 0 6px; margin-bottom: 0; }
.support-ticket-dialog .el-select, .support-ticket-dialog .el-date-editor { width: 100%; }
.support-ticket-dialog .ticket-checkbox { display: flex; white-space: normal; }
.support-ticket-dialog .ticket-checkbox .el-checkbox__label { white-space: normal; line-height: 1.5; }
.support-ticket-dialog .ticket-description { margin: 5px 0 0; color: #666; font-size: 12px; line-height: 1.5; }
.support-ticket-dialog .ticket-error { color: #c0392b; }
</style>
